import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AuthModal({ open, onOpenChange, initialMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setMode(initialMode); }, [initialMode, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back, partner!");
      } else {
        await register(name, email, password);
        toast.success("Saddle up — you're in!");
      }
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Somethin' went sideways. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-modal"
        className="bg-[#151A14] border-2 border-[#2C332A] text-[#E5E5E5] rounded-none max-w-md p-0">
        <div className="diag-stripes h-2 w-full" />
        <div className="p-6 md:p-8">
          <DialogHeader>
            <DialogTitle className="font-anton text-3xl uppercase tracking-tight">
              {mode === "login" ? "Sign In" : "Join the Posse"}
            </DialogTitle>
            <DialogDescription className="text-[#8D9688]">
              {mode === "login" ? "Welcome back to the back roads." : "Create an account and saddle up."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="uppercase text-xs tracking-widest text-[#8D9688]">Name</Label>
                <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="bg-[#0A0E0B] border-2 border-[#2C332A] focus:border-[#DD5422] rounded-none mt-1 h-12" />
              </div>
            )}
            <div>
              <Label className="uppercase text-xs tracking-widest text-[#8D9688]">Email</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-[#0A0E0B] border-2 border-[#2C332A] focus:border-[#DD5422] rounded-none mt-1 h-12" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-widest text-[#8D9688]">Password</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="bg-[#0A0E0B] border-2 border-[#2C332A] focus:border-[#DD5422] rounded-none mt-1 h-12" />
            </div>
            <Button data-testid="auth-submit-btn" disabled={loading} type="submit"
              className="w-full bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest text-lg h-12 border-2 border-[#DD5422]">
              {loading ? "Loadin'..." : (mode === "login" ? "Sign In" : "Sign Up")}
            </Button>
            <div className="text-center text-sm text-[#8D9688]">
              {mode === "login" ? (
                <>No account? <button type="button" data-testid="switch-to-register" className="text-[#DD5422] uppercase tracking-widest font-bold" onClick={() => setMode("register")}>Sign Up</button></>
              ) : (
                <>Already in? <button type="button" data-testid="switch-to-login" className="text-[#DD5422] uppercase tracking-widest font-bold" onClick={() => setMode("login")}>Sign In</button></>
              )}
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
